import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import xlsx from "json-as-xlsx";
import { XLSX } from "../../config/constants";
import {
  customerExcelData2DBData,
  getFieldErrors,
  IFieldError,
  interpretDataIntoXlsxFormat,
} from "../helpers";
import { IUser, tryConfirmLogin } from "./authReducer";

class ExportCustomersErrs {
  form: string | null = null;
}

interface IEnumCompany {
  _id: string;
  name: string;
  email: string;
  phoneNumber: string;
  workersCount: number;
  sitesCount: number;
}

export interface IEnumCustomer {
  _id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  imgSrc: string;
  emailVerified: boolean;
  status: string;
  has2FactorAuth: boolean;
}

export interface ICustomer extends IUser {
  userId: string;
  phoneNumber?: string;
  deliveryAddress: string;
  ordersCount: number;
  orderRequestsCount: number;
  creditBalance: number;
  company?: IEnumCompany;
}

export interface IEnumCustomer {
  _id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  imgSrc: string;
  emailVerified: boolean;
  status: string;
  has2FactorAuth: boolean;
}

class RegCustomerErrs {
  index?: any;

  form: string | null = null;
  firstName: string | null = null;
  lastName: string | null = null;
  usrImg: string | null = null;
  creditBalance: string | null = null;
  email: string | null = null;
  phoneNumber: string | null = null;
  landLine: string | null = null;
  deliveryAddress: string | null = null;

  compName: string | null = null;
  compCountry: string | null = null;
  compState: string | null = null;
  compMainOfficeAddress: string | null = null;
  compEmail: string | null = null;
  compWebsite: string | null = null;
  compPhoneNumber: string | null = null;
  compFaxNumber: string | null = null;
}

// type ImportCustomersErrs = RegCustomerErrs[];

export interface ICustomersState {
  registeringCustomer: boolean;
  regCustomerErrs: RegCustomerErrs;
  fetchingCustomers: boolean;
  fetchCustomersErr: string | null;
  customers: ICustomer[];
  fetchingMainCustomer: boolean;
  fetchMainCustomerErr: string | null;
  mainCustomerId: string | null;
  mainCustomer: ICustomer | null;
  updatingCustomer: boolean;
  updatedCustomer: boolean;
  updateCustomerErrs: RegCustomerErrs;
  importingCustomers: boolean;
  triedImportingCustomers: boolean | null;
  importedCustomers: boolean | null;
  importCustomersErrs: RegCustomerErrs[];
  exportingCustomers: boolean | null;
  exportedCustomers: boolean | null;
  exportCustomersErrs: ExportCustomersErrs;
}

const initialState = {
  registeringCustomer: false,
  regCustomerErrs: new RegCustomerErrs(),
  fetchingCustomers: false,
  fetchCustomersErr: null,
  customers: [],
  // mainCustomer: {
  //     _id: 'oiwewioidecd',
  //     userId: 'wewewe',
  //     firstName: 'Olamide',
  //     lastName: 'Falade',
  //     imgSrc: '',
  //     deliveryAddress: 'wwew weionwedwe',
  //     phoneNumber: 'wewewewewew',
  //     email: 'ofalade326@stu.ui.edu.ng',
  //     orderRequestsCount: 2,
  //     ordersCount: 3,
  //     emailVerified: true,
  //     creditBalance: 12
  // },
  fetchingMainCustomer: false,
  fetchMainCustomerErr: null,
  mainCustomerId: null,
  mainCustomer: null,
  updatingCustomer: false,
  updatedCustomer: false,
  updateCustomerErrs: new RegCustomerErrs(),
  importingCustomers: false,
  importedCustomers: false,
  importCustomersErrs: [],
  exportingCustomers: false,
  triedImportingCustomers: false,
  exportedCustomers: false,
  exportCustomersErrs: new ExportCustomersErrs(),
} as ICustomersState;

const customersSlice = createSlice({
  name: "customers",
  initialState,
  reducers: {
    clearRegCustomerFieldErr: (
      state: ICustomersState,
      action: PayloadAction<string>
    ) => {
      state.regCustomerErrs[action.payload as keyof RegCustomerErrs] = null;
    },
    clearRegCustomerErrs: (state: ICustomersState) => {
      state.regCustomerErrs = new RegCustomerErrs();
    },
    tryResetRegCustomerFormErr: (state: ICustomersState) => {
      let shouldReset = true;

      for (let key in state.regCustomerErrs) {
        if (key === "form") {
          continue;
        }

        if (state.regCustomerErrs[key as keyof RegCustomerErrs]) {
          shouldReset = false;
          break;
        }
      }

      if (shouldReset) {
        state.regCustomerErrs.form = null;
      }
    },
    regCustomerStart: (state: ICustomersState) => {
      state.regCustomerErrs = new RegCustomerErrs();
      state.registeringCustomer = true;
    },
    regCustomerPass: (state: ICustomersState) => {
      state.registeringCustomer = false;
    },
    regCustomerFail: (
      state: ICustomersState,
      action: PayloadAction<IFieldError[]>
    ) => {
      state.registeringCustomer = false;

      const errs = action.payload;

      for (const err of errs) {
        for (const key in state.regCustomerErrs) {
          if (err.field === key) {
            state.regCustomerErrs[key as keyof RegCustomerErrs] = err.msg;
            break;
          }
        }
      }

      if (!state.regCustomerErrs.form) {
        state.regCustomerErrs.form = "Form incorrectly filled.";
      }
    },
    fetchCustomerStart: (state: ICustomersState) => {
      state.fetchingCustomers = true;
    },
    fetchCustomerPass: (
      state: ICustomersState,
      action: PayloadAction<ICustomer[]>
    ) => {
      state.fetchingCustomers = false;

      console.log(action.payload);

      state.customers = action.payload;
    },
    fetchCustomerFail: (
      state: ICustomersState,
      action: PayloadAction<string>
    ) => {
      state.fetchingCustomers = false;
      state.fetchCustomersErr = action.payload;
    },
    clearFetchMainCustomerErr: (state: ICustomersState) => {
      state.fetchMainCustomerErr = null;
    },
    fetchMainCustomerStart: (state: ICustomersState) => {
      state.fetchingMainCustomer = true;
      state.fetchMainCustomerErr = null;
    },
    fetchMainCustomerPass: (
      state: ICustomersState,
      action: PayloadAction<ICustomer>
    ) => {
      state.fetchingMainCustomer = false;
      state.mainCustomer = action.payload;
    },
    fetchMainCustomerFail: (
      state: ICustomersState,
      action: PayloadAction<string>
    ) => {
      state.fetchingMainCustomer = false;
      state.fetchMainCustomerErr = action.payload;
    },
    setMainCustomerId: (
      state: ICustomersState,
      action: PayloadAction<string | null>
    ) => {
      state.mainCustomerId = action.payload;
    },
    setMainCustomer: (
      state: ICustomersState,
      action: PayloadAction<ICustomer | null>
    ) => {
      state.mainCustomer = action.payload;
    },
    clearUpdateCustomerFieldErr: (
      state: ICustomersState,
      action: PayloadAction<string>
    ) => {
      state.updateCustomerErrs[action.payload as keyof RegCustomerErrs] = null;
    },
    clearUpdateCustomerErrs: (state: ICustomersState) => {
      state.updateCustomerErrs = new RegCustomerErrs();
    },
    tryResetUpdateCustomerFormErr: (state: ICustomersState) => {
      let shouldReset = true;

      for (let key in state.updateCustomerErrs) {
        if (key === "form") {
          continue;
        }

        if (state.updateCustomerErrs[key as keyof RegCustomerErrs]) {
          shouldReset = false;
          break;
        }
      }

      if (shouldReset) {
        state.updateCustomerErrs.form = null;
      }
    },
    resetUpdatedCustomer: (state: ICustomersState) => {
      state.updatedCustomer = false;
    },
    updateCustomerStart: (state: ICustomersState) => {
      state.updatedCustomer = false;
      state.updateCustomerErrs = new RegCustomerErrs();
      state.updatingCustomer = true;
    },
    updateCustomerPass: (
      state: ICustomersState,
      action: PayloadAction<ICustomer>
    ) => {
      state.updatingCustomer = false;
      state.updatedCustomer = true;
    },
    updateCustomerFail: (
      state: ICustomersState,
      action: PayloadAction<IFieldError[]>
    ) => {
      state.updatingCustomer = false;

      const errs = action.payload;

      for (const err of errs) {
        for (const key in state.updateCustomerErrs) {
          if (err.field === key) {
            state.updateCustomerErrs[key as keyof RegCustomerErrs] = err.msg;
            break;
          }
        }
      }

      if (!state.updateCustomerErrs.form) {
        state.updateCustomerErrs.form = "Form incorrectly filled.";
      }
    },
    clearExportCustomersFieldErr: (
      state: ICustomersState,
      action: PayloadAction<string>
    ) => {
      state.exportCustomersErrs[action.payload as keyof ExportCustomersErrs] =
        null;
    },
    clearExportCustomersErrs: (state: ICustomersState) => {
      state.exportCustomersErrs = new ExportCustomersErrs();
    },
    tryResetExportCustomersFormErr: (state: ICustomersState) => {
      let shouldReset = true;

      for (let key in state.exportCustomersErrs) {
        if (key === "form") {
          continue;
        }

        if (state.exportCustomersErrs[key as keyof ExportCustomersErrs]) {
          shouldReset = false;
          break;
        }
      }

      if (shouldReset) {
        state.exportCustomersErrs.form = null;
      }
    },
    exportCustomersStart: (state: ICustomersState) => {
      state.exportingCustomers = true;
      state.exportedCustomers = false;
    },
    exportCustomersPass: (state: ICustomersState) => {
      state.exportingCustomers = false;
      state.exportedCustomers = true;
    },
    exportCustomersFail: (
      state: ICustomersState,
      action: PayloadAction<IFieldError[]>
    ) => {
      state.exportingCustomers = false;

      const errs = action.payload;

      for (const err of errs) {
        for (const key in state.exportCustomersErrs) {
          if (err.field === key) {
            state.exportCustomersErrs[key as keyof ExportCustomersErrs] =
              err.msg;
            break;
          }
        }
      }

      if (!state.exportCustomersErrs.form) {
        state.exportCustomersErrs.form = "Form incorrectly filled.";
      }
    },
    clearImportCustomersFieldErr: (
      state: ICustomersState,
      action: PayloadAction<string>
    ) => {
      state.importCustomersErrs = [];
    },
    clearImportCustomersErrByIndex: (
      state: ICustomersState,
      action: PayloadAction<number>
    ) => {
      const index = action.payload;

      let newErrs = [];

      if (index < state.importCustomersErrs.length) {
        for (let i = 0; i < index; i++) {
          newErrs[i] = state.importCustomersErrs[i];
        }

        for (let i = index; i < state.importCustomersErrs.length - 1; i++) {
          newErrs[i] = state.importCustomersErrs[i + 1];
        }

        state.importCustomersErrs = newErrs;
      }
    },
    clearResetTriedImportingCustomers: (state: ICustomersState) => {
      state.triedImportingCustomers = false;
    },
    clearImportCustomersErrs: (state: ICustomersState) => {
      state.importCustomersErrs = [];
    },
    tryResetImportCustomersFormErr: (state: ICustomersState) => {},
    importCustomersStart: (state: ICustomersState) => {
      state.importCustomersErrs = [];

      state.importingCustomers = true;
      state.triedImportingCustomers = false;
      state.importedCustomers = false;
    },
    importCustomersDone: (state: ICustomersState) => {
      state.importingCustomers = false;
      state.triedImportingCustomers = true;
    },
    importCustomersPass: (state: ICustomersState) => {
      state.importingCustomers = false;
      state.triedImportingCustomers = true;
      state.importedCustomers = true;
    },
    addErr2ImportCustomersErrs: (
      state: ICustomersState,
      action: PayloadAction<RegCustomerErrs>
    ) => {
      let errs = state.importCustomersErrs;

      errs = [...errs, action.payload];

      state.importCustomersErrs = errs;
    },
    importCustomersFail: (state: ICustomersState) => {
      state.importingCustomers = false;
    },
  },
});

export const {
  clearRegCustomerFieldErr,
  clearRegCustomerErrs,
  tryResetRegCustomerFormErr,
  regCustomerStart,
  regCustomerPass,
  regCustomerFail,
  fetchCustomerStart,
  fetchCustomerPass,
  fetchCustomerFail,
  clearFetchMainCustomerErr,
  fetchMainCustomerStart,
  fetchMainCustomerPass,
  fetchMainCustomerFail,
  setMainCustomer,
  setMainCustomerId,
  clearUpdateCustomerFieldErr,
  clearUpdateCustomerErrs,
  tryResetUpdateCustomerFormErr,
  resetUpdatedCustomer,
  updateCustomerStart,
  updateCustomerPass,
  updateCustomerFail,
  clearExportCustomersErrs,
  clearExportCustomersFieldErr,
  tryResetExportCustomersFormErr,
  exportCustomersStart,
  exportCustomersPass,
  exportCustomersFail,
  clearResetTriedImportingCustomers,
  clearImportCustomersErrs,
  clearImportCustomersFieldErr,
  tryResetImportCustomersFormErr,
  importCustomersStart,
  importCustomersDone,
  importCustomersPass,
  addErr2ImportCustomersErrs,
  importCustomersFail,
} = customersSlice.actions;

interface ITryRegCustomerArg {
  data: FormData;
  authToken: string;
  setRegistered: (truth: boolean) => void;
}

export const tryRegCustomer = createAsyncThunk(
  "customers/tryRegCustomer",
  (arg: ITryRegCustomerArg, { dispatch }) => {
    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/customer`;

    const { data, authToken, setRegistered } = arg;

    dispatch(regCustomerStart());

    axios
      .post(endpoint, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        dispatch(regCustomerPass());
        setRegistered(true);
      })
      .catch((err) => {
        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
          fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
          fieldErrors = [{ field: "form", msg: data.err }];
        } else {
          fieldErrors = [
            { field: "form", msg: "Check your network connection." },
          ];
        }

        dispatch(regCustomerFail(fieldErrors));
      });
  }
);

interface ITryFetchCustomerArg {
  authToken: string;
  companyId?: string;
}

export const tryFetchCustomers = createAsyncThunk(
  "customers/tryFetchCustomers",
  (arg: ITryFetchCustomerArg, { dispatch }) => {
    const { authToken, companyId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/customers${
      companyId ? `?companyId=${companyId}` : ""
    }`;

    dispatch(fetchCustomerStart());
    axios
      .get(endpoint, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        dispatch(fetchCustomerPass(res.data));
      })
      .catch((err) => {
        const data = err?.response?.data;

        if (data?.err && data.err === "Session expired") {
          dispatch(tryConfirmLogin({ authToken }));
          return;
        }

        if (data?.err) {
          dispatch(fetchCustomerFail(data.err));
        } else {
          dispatch(fetchCustomerFail("Check your network connection."));
        }
      });
  }
);

interface ITryFetchMainCustomerArg {
  authToken: string;
  customerId: string;
}

export const tryFetchMainCustomer = createAsyncThunk(
  "customers/tryFetchMainCustomer",
  (arg: ITryFetchMainCustomerArg, { dispatch }) => {
    const { authToken, customerId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/customers/${customerId}`;

    dispatch(fetchMainCustomerStart());
    axios
      .get(endpoint, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        const data = res.data;

        dispatch(fetchMainCustomerPass(res.data));
      })
      .catch((err) => {
        const data = err?.response?.data;

        if (data?.err && data.err === "Session expired") {
          dispatch(tryConfirmLogin({ authToken }));
          return;
        }

        if (data?.errors) {
          dispatch(fetchMainCustomerFail(getFieldErrors(data.errors)[0].msg));
        } else if (data?.err) {
          dispatch(fetchMainCustomerFail(data.err));
        } else {
          dispatch(fetchMainCustomerFail("Check your network connection."));
        }
      });
  }
);

interface ITryUpdateCustomerArg {
  userId: string;
  data: FormData;
  authToken: string;
  setUpdated: (truth: boolean) => void;
}

export const tryUpdateCustomer = createAsyncThunk(
  "customers/tryUpdateCustomer",
  (arg: ITryUpdateCustomerArg, { dispatch }) => {
    const { userId, data, authToken, setUpdated } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/customers/${userId}`;

    dispatch(updateCustomerStart());

    axios
      .put(endpoint, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        dispatch(updateCustomerPass(res.data));
        setUpdated(true);
      })
      .catch((err) => {
        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
          fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
          fieldErrors = [{ field: "form", msg: data.err }];
        } else {
          fieldErrors = [
            { field: "form", msg: "Check your network connection." },
          ];
        }

        dispatch(updateCustomerFail(fieldErrors));
      });
  }
);

interface ITryExportCustomersArg {
  format: string;
  authToken: string;
}

export const tryExportCustomers = createAsyncThunk(
  "customers/tryExportCustomers",
  (arg: ITryExportCustomersArg, { dispatch }) => {
    const { authToken, format } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/customers-export`;

    dispatch(exportCustomersStart());
    axios
      .get(endpoint, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        const data = res.data;

        if (data.length === 0) {
          const fieldErrors = [{ field: "form", msg: "Export Empty." }];

          dispatch(exportCustomersFail(fieldErrors));
        } else {
          switch (format) {
            case XLSX:
            default:
              const fileSettings = {
                fileName: "customers", //TODO: dynamically generate the name, based on the parameters used to fetch the data
              };

              try {
                xlsx(interpretDataIntoXlsxFormat(data), fileSettings);
              } catch (err) {
                alert("err");
                console.log(err);
              }
              break;
          }

          dispatch(exportCustomersPass());
        }
      })
      .catch((err) => {
        const data = err?.response?.data;

        if (data?.err && data.err === "Session expired") {
          dispatch(tryConfirmLogin({ authToken }));
          return;
        }

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
          fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
          fieldErrors = [{ field: "form", msg: data.err }];
        } else {
          console.log(data);
          fieldErrors = [
            { field: "form", msg: "Check your network connection." },
          ];
        }

        dispatch(exportCustomersFail(fieldErrors));
      });
  }
);

interface ITryImportCustomersArg {
  format: string;
  authToken: string;
  data: any[];
}

export const tryImportCustomers = createAsyncThunk(
  "customers/tryImportCustomers",
  (arg: ITryImportCustomersArg, { dispatch }) => {
    const { authToken, format, data } = arg;

    let errors: RegCustomerErrs[] = [];

    let itr = 0;
    let successCount = 0;

    for (const userData of data) {
      dispatch(importCustomersStart());

      const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/customer`;

      let data4From: any = null;

      try {
        switch (format) {
          case XLSX:
          default:
            data4From = customerExcelData2DBData(userData);
            break;
        }
      } catch (err) {
        dispatch(importCustomersFail());
        alert(`Error : ${err}`);
        return;
      }

      let newData = new FormData();
      let registered = false;

      for (const key in data4From) {
        newData.append(key, data4From[key as keyof typeof data4From]);
      }

      axios
        .post(endpoint, newData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${authToken}`,
          },
        })
        .then((res) => {
          ++successCount;
          dispatch(importCustomersDone());
        })
        .catch((err) => {
          const errData = err?.response?.data;

          let fieldErrors: IFieldError[] = [];

          if (errData?.errors) {
            fieldErrors = getFieldErrors(errData.errors);
          } else if (errData?.err) {
            fieldErrors = [{ field: "form", msg: errData.err }];
          } else {
            fieldErrors = [
              { field: "form", msg: "Check your network connection." },
            ];
          }

          let regErrs = new RegCustomerErrs();

          for (const error of fieldErrors) {
            for (const key in regErrs) {
              if (error.field === key) {
                regErrs[key as keyof RegCustomerErrs] = error.msg;
                break;
              }
            }
          }

          regErrs.index = itr;

          dispatch(addErr2ImportCustomersErrs(regErrs));

          if (itr++ === data.length - 1) {
            itr = 0;
            dispatch(importCustomersDone());
          }
        });
    }
  }
);

export default customersSlice.reducer;
